import React from 'react'
import { TaskListItem } from '@myapp/contract'
import { getLeftPercent, getWidthPercent, TASK_COLORS, COMPLETED_TASK_COLORS, getTaskColors } from './timelineConstants'
import { SegmentedBlock, Segment } from './SegmentedBlock'
import { formatTimeHHmm } from '../../../shared/lib/timeline/dateHelpers'

interface Props {
  task: TaskListItem
  onClick?: () => void
  onDragStart?: (e: React.MouseEvent) => void
  isDragging?: boolean
  isEditing?: boolean
  isTransferTravel?: boolean
  isTransferDeparture?: boolean
  laneIndex?: number
  laneCount?: number
}

export const TaskBlock = ({ task, onClick, onDragStart, isDragging, isEditing, isTransferTravel, isTransferDeparture, laneIndex, laneCount }: Props) => {
  const isInProgress = !!task.actualStart && !task.actualEnd
  const useActuals = task.status === 'COMPLETED' || isInProgress

  const travelDuration = useActuals ? (task.actualTravelDuration ?? task.plannedTravelDuration) : task.plannedTravelDuration
  const workDuration = useActuals ? (task.actualWorkDuration ?? task.plannedWorkDuration) : task.plannedWorkDuration
  const departureDuration = useActuals ? (task.actualDepartureDuration ?? task.plannedDepartureDuration) : task.plannedDepartureDuration

  const workStart = useActuals && task.actualStart
    ? new Date(task.actualStart)
    : new Date(task.plannedStart!)
  const anfahrtStart = new Date(workStart.getTime() - travelDuration * 60_000)

  const totalDuration = travelDuration + workDuration + departureDuration

  const left = getLeftPercent(anfahrtStart.toISOString())
  const width = getWidthPercent(totalDuration)

  const abfahrtStart = new Date(workStart.getTime() + workDuration * 60_000)
  const timeLabel = formatTimeHHmm(workStart.toISOString())

  const { workColor, travelColor } = getTaskColors(task.status)
  const effectiveTravelColor = isInProgress ? COMPLETED_TASK_COLORS.travel : travelColor

  const segments: Segment[] = [
    { duration: travelDuration, color: effectiveTravelColor, topLabel: formatTimeHHmm(anfahrtStart.toISOString()), label: isTransferTravel ? 'Transfer Anfahrt' : 'Anfahrt', subLabel: `${travelDuration} min` },
    { duration: workDuration, color: workColor, topLabel: timeLabel, label: task.title, pulsing: isInProgress, statusPill: isInProgress ? 'Läuft' : undefined },
    { duration: departureDuration, color: travelColor, topLabel: formatTimeHHmm(abfahrtStart.toISOString()), label: isTransferDeparture ? 'Transfer Abfahrt' : 'Abfahrt', subLabel: `${departureDuration} min` },
  ]

  return (
    <SegmentedBlock
      segments={segments}
      left={left}
      width={width}
      laneIndex={laneIndex}
      laneCount={laneCount}
      className={`border-r-2 border-white cursor-grab ${isDragging || isEditing ? 'opacity-30' : ''} ${isEditing ? 'pointer-events-none' : ''}`}
      style={{ marginRight: 2 }}
      onMouseDown={(e) => {
        e.stopPropagation()
        onDragStart?.(e)
        if (!onDragStart) onClick?.()
      }}
    />
  )
}

export const TaskGhostBlock = ({ anfahrtStartIso, durations, isColliding }: {
  anfahrtStartIso: string
  durations: { plannedTravelDuration: number; plannedWorkDuration: number; plannedDepartureDuration: number }
  isColliding?: boolean
}) => {
  const totalDuration = durations.plannedTravelDuration + durations.plannedWorkDuration + durations.plannedDepartureDuration
  const left = getLeftPercent(anfahrtStartIso)
  const width = getWidthPercent(totalDuration)

  const collisionColor = '#ef4444'
  const segments: Segment[] = isColliding
    ? [
        { duration: durations.plannedTravelDuration, color: collisionColor, label: 'Anfahrt' },
        { duration: durations.plannedWorkDuration, color: collisionColor },
        { duration: durations.plannedDepartureDuration, color: collisionColor, label: 'Abfahrt' },
      ]
    : [
        { duration: durations.plannedTravelDuration, color: TASK_COLORS.travel, label: 'Anfahrt' },
        { duration: durations.plannedWorkDuration, color: TASK_COLORS.work },
        { duration: durations.plannedDepartureDuration, color: TASK_COLORS.travel, label: 'Abfahrt' },
      ]

  return (
    <SegmentedBlock
      segments={segments}
      left={left}
      width={width}
      className="opacity-60 pointer-events-none ring-2 ring-white ring-inset rounded"
      style={{ marginRight: 2 }}
    />
  )
}
